import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tab,
  Tabs,
  TablePagination,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Request from "../Utils/Request/Request";
import DeleteModal from "./DeleteModal";
import { AppStorage } from "../Utils/Context/AppContext";
import { showToast } from "../Utils";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DuplicateModal from "../modals/DuplicateModal";
import TemplateImages from "./TemplateImages";
import SearchIcon from "@mui/icons-material/Search";

const TemplatesListContent = () => {
  const [templates, setTemplates] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const AppContext = useContext(AppStorage);

  const navigate = useNavigate();

  const [delTemp, setDelTemp] = useState("");
  const [openDelModal, setOpenDelModal] = useState(false);
  const [openDuplicateModal, setOpenDuplicateModal] = useState(false);
  const [dupTemp, setDupTemp] = useState("");

  const filteredTemplates = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    if (!query) return templates;
    return templates.filter((template) =>
      String(template.name || "").toLocaleLowerCase().includes(query)
    );
  }, [search, templates]);

  const paginatedTemplates = useMemo(
    () => filteredTemplates.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredTemplates, page, rowsPerPage]
  );

  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(filteredTemplates.length / rowsPerPage) - 1);
    if (page > lastPage) setPage(lastPage);
  }, [filteredTemplates.length, page, rowsPerPage]);

  const OpenDelModal = (user) => {
    setOpenDelModal(true);
    setDelTemp(user);
  };

  const CloseDelModal = () => {
    setOpenDelModal(false);
  };
  const fetchTemplates = async () => {
    try {
      const response = await Request.get("/getAllTemplates");
      setTemplates(response.data);
    } catch (error) {
      console.error("Error fetching templates: ", error);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [AppContext?.userAuthToken]);

  const handleTemplateClick = (id) => {
    const template = templates.find((item) => item.id === id);
    navigate(template?.templateType === "designer" ? `/template-designer/${id}` : `/template-builder/${id}`);
  };

  const handleDeleteTemplate = async (temp) => {
    try {
      await Request.delete(`/deleteTemplate/${temp["id"]}`);
      fetchTemplates();
      showToast("success", "Deleted Successfully.");
      CloseDelModal();
    } catch (error) {
      console.error("Error deleting template: ", error);
    }
  };
  function duplicateTemplate(temp) {
    setOpenDuplicateModal(true);
    setDupTemp(temp);
  }

  function navigateDuplicateTemplate(temp) {
    navigate(`/template-duplicate/${temp.id}`);
  }
  return (
    <Box sx={{ px: 5 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <TextField
          size="small"
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(0);
          }}
          placeholder="Search templates"
          inputProps={{ "aria-label": "Search templates" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ),
          }}
          sx={{ width: { xs: "100%", sm: 320 } }}
        />
      </Box>
      <TableContainer>
        <Table sx={{ tableLayout: "fixed", minWidth: 420 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell sx={{ width: 145 }}>Editor</TableCell>
              <TableCell align="center" sx={{ width: 168 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedTemplates.map((template) => (
              <TableRow key={template.id}>
                <TableCell sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    title={template.name}
                    sx={{ overflow: "hidden", textOverflow: "ellipsis" }}
                  >
                    {template.name}
                  </Typography>
                </TableCell>
                <TableCell>{template.templateType === "designer" ? "Template Designer" : "Template Builder"}</TableCell>
                <TableCell align="center" sx={{ width: 168, whiteSpace: "nowrap" }}>
                  <IconButton
                    onClick={() => handleTemplateClick(template.id)}
                    aria-label="edit"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => duplicateTemplate(template)}
                    aria-label="duplicate"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                  <IconButton
                    onClick={() => OpenDelModal(template)}
                    aria-label="delete"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {paginatedTemplates.length === 0 && (
              <TableRow>
                <TableCell colSpan={3} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {search ? "No templates match your search." : "No templates found."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filteredTemplates.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(event) => {
          setRowsPerPage(Number(event.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[5, 10, 25]}
      />
      {openDelModal && (
        <DeleteModal
          del={handleDeleteTemplate}
          currentUser={delTemp}
          openDelModal={openDelModal}
          setDeleteModal={setOpenDelModal}
        />
      )}
      {openDuplicateModal && (
        <DuplicateModal
          currentUser={dupTemp}
          openDuplicateModal={openDuplicateModal}
          setDuplicateModal={setOpenDuplicateModal}
          duplicateUser={navigateDuplicateTemplate}
        />
      )}
    </Box>
  );
};

const TemplateList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") === "images" ? 1 : 0);
  const navigate = useNavigate();

  const handleTabChange = (_, value) => {
    setActiveTab(value);
    setSearchParams(value === 1 ? { tab: "images" } : {});
  };

  return (
    <Box>
      <Box sx={{ px: 5, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mb: 2, flexWrap: "wrap" }}>
        <Typography variant="h4" sx={{ color: "#365b6d" }}>Invoice Templates</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="outlined" onClick={() => navigate("/template-builder")}>Open Template Builder</Button>
          <Button variant="contained" onClick={() => navigate("/template-designer")}>Open Template Designer</Button>
        </Box>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          aria-label="Invoice template sections"
          sx={{
            "& .MuiTab-root": { fontWeight: 700, textTransform: "none" },
            "& .Mui-selected": { color: "#365b6d !important" },
            "& .MuiTabs-indicator": { backgroundColor: "#c79a45" },
          }}
        >
          <Tab label="Templates" />
          <Tab label="Template Images" />
        </Tabs>
      </Box>

      <Box role="tabpanel" hidden={activeTab !== 0}>
        {activeTab === 0 && <TemplatesListContent />}
      </Box>
      <Box role="tabpanel" hidden={activeTab !== 1}>
        {activeTab === 1 && <TemplateImages />}
      </Box>
    </Box>
  );
};

export default TemplateList;
